import { useEffect, useRef, useState, useCallback } from 'react';
import './BlogFeed.css';
import { BlogCard } from './BlogCard';
import { usePrefetchTrigger } from '../hooks/useBlogFeed';
import type { BlogFeedItem } from '../types';

interface BlogFeedProps {
  items: BlogFeedItem[];
  isLoading: boolean;
  isFetchingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  fetchMore: () => void;
  retry: () => void;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

function SkeletonCard() {
  return (
    <div className="blog-feed__skeleton-card" aria-hidden="true">
      <div className="blog-feed__skeleton-inner">
        <div className="blog-feed__skeleton-line loading-shimmer" style={{ width: '80px', height: '20px' }} />
        <div className="blog-feed__skeleton-line loading-shimmer" style={{ width: '60px', height: '52px' }} />
        <div className="blog-feed__skeleton-line loading-shimmer" style={{ width: '100%', height: '28px' }} />
        <div className="blog-feed__skeleton-line loading-shimmer" style={{ width: '90%', height: '20px' }} />
        <div className="blog-feed__skeleton-line loading-shimmer" style={{ width: '100%', height: '80px' }} />
        <div className="blog-feed__skeleton-line loading-shimmer" style={{ width: '100%', height: '60px' }} />
      </div>
    </div>
  );
}

export function BlogFeed({
  items,
  isLoading,
  isFetchingMore,
  error,
  hasMore,
  fetchMore,
  retry,
  scrollContainerRef,
}: BlogFeedProps) {
  const fallbackRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? fallbackRef;
  const [currentIndex, setCurrentIndex] = useState(0);
  const firstCardRef = useRef<HTMLElement>(null);

  // Track which card is currently snapped into view
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    const firstCard = firstCardRef.current;
    if (!container || !firstCard) return;
    const cardHeight = window.innerHeight;
    if (cardHeight === 0) return;
    // Calculate offset from the first card's top
    const headerOffset = firstCard.offsetTop;
    const scrollFromCards = container.scrollTop - headerOffset;
    const index = Math.max(0, Math.round(scrollFromCards / cardHeight));
    setCurrentIndex(Math.min(index, items.length - 1));
  }, [items.length, scrollRef]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, scrollRef]);

  // Keyboard navigation: arrow up/down
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const container = scrollRef.current;
      const firstCard = firstCardRef.current;
      if (!container || !firstCard) return;
      const headerOffset = firstCard.offsetTop;
      const cardHeight = window.innerHeight;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = Math.min(currentIndex + 1, items.length - 1);
        container.scrollTo({ top: headerOffset + nextIndex * cardHeight, behavior: 'smooth' });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = Math.max(currentIndex - 1, 0);
        container.scrollTo({ top: prevIndex === 0 ? headerOffset : headerOffset + prevIndex * cardHeight, behavior: 'smooth' });
      }
    },
    [currentIndex, items.length, scrollRef]
  );

  // Prefetch when 3 items from end
  usePrefetchTrigger(currentIndex, items.length, fetchMore, hasMore, isFetchingMore);

  if (isLoading) {
    return (
      <div className="blog-feed" aria-busy="true" aria-label="Loading blog articles">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="blog-feed__error" role="alert">
        <p className="blog-feed__error-title">Could not load articles</p>
        <p className="blog-feed__error-message">{error.message}</p>
        <button className="blog-feed__retry-btn" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="blog-feed__empty">
        <span className="blog-feed__empty-icon" aria-hidden="true">📰</span>
        <p className="blog-feed__empty-title">Nothing here yet</p>
        <p className="blog-feed__empty-body">
          No blog summaries for today yet. Check back later.
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        ref={!scrollContainerRef ? fallbackRef : undefined}
        className="blog-feed"
        role="listbox"
        aria-label="Blog articles feed — use arrow keys to navigate"
        aria-busy={isFetchingMore}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {items.map((item, index) => (
          <BlogCard
            key={item.summaryId}
            item={item}
            index={index}
            ref={index === 0 ? firstCardRef : undefined}
          />
        ))}

        {isFetchingMore && (
          <div className="blog-feed__loading-more" aria-label="Loading more articles">
            <div className="blog-feed__spinner" role="status">
              <span className="visually-hidden">Loading more articles...</span>
            </div>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div
          className="blog-feed__indicator"
          aria-live="polite"
          aria-label={`Article ${currentIndex + 1} of ${items.length}`}
        >
          {currentIndex + 1} of {items.length}
        </div>
      )}
    </>
  );
}
